/**
 * Create a persona — two steps, and step 2 picks GROUPS.
 *
 * Two steps; the `admin` role skips step 2 entirely (an admin bypasses
 * every permission check, so anything picked there is a no-op).
 *
 * NO PASSWORD (2026-11-03, Pablo). The person is mailed an invitation
 * from noreply@omnitok.com and chooses their own password; nobody else
 * sees it. The old typed/generated password and its copy button are
 * gone from here. Handing a password over by hand is still possible, as
 * a separate action: «Restablecer contraseña» on the person's row
 * (`ResetPasswordModal`), which shows a server-generated one once.
 *
 * The result of the mail is reported to the caller (`onCreated(mail)`):
 * a failed send keeps the person — the screen says so and «Reenviar
 * invitación» tries again.
 *
 * THE ONE CHANGE
 * --------------
 * Step 2 picks groups instead of report pills. Pablo confirmed there are
 * no individual permissions, so `initial_subscriptions` has no target to
 * write to any more: subscriptions become `(dashboard, view)` cells of a
 * GROUP, and groups are the only thing a new person can be given. The
 * field is therefore not sent at all, rather than sent empty.
 *
 * A person created with NO group can sign in and see nothing. That is
 * the honest outcome and the callout says it, instead of the wizard
 * quietly adding them to something.
 */

import { Button, Input, Modal, Select, type SelectOption } from "@omnitok/ui";
import { Callout, FormField } from "@pervasivemind/admin-ui";
import { Check, Mail, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import { SCOPE_ALL, groupsApi, type GroupSummaryOut } from "@/lib/access-api";
import {
  ADMIN_USER_ROLES,
  adminUsersApi,
  type AccessMail,
  type AdminUserRole,
} from "@/lib/admin-users-api";

/** The invitation mail's outcome, for the page to show. */
export interface CreatedPerson {
  email: string;
  mail: AccessMail;
}

/** Create the person ONCE, then join every picked group. The id is
 *  reported the moment it exists, so a retry after a failed join
 *  re-joins (a 200 no-op for groups already joined) instead of
 *  re-creating — which the server refuses with a 409. */
export async function createThenJoin(
  createdId: string | null,
  create: () => Promise<{ id: string }>,
  addMember: (groupId: string, userId: string) => Promise<unknown>,
  groupIds: readonly string[],
  onCreatedId: (id: string) => void,
): Promise<void> {
  let userId: string;
  if (createdId === null) {
    userId = (await create()).id;
    onCreatedId(userId);
  } else {
    userId = createdId;
  }
  for (const groupId of groupIds) {
    await addMember(groupId, userId);
  }
}

export function CreatePersonWizard({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** `null` when the modal closes after a retry that found the person
   *  already created (their mail was reported the first time). */
  onCreated: (created: CreatedPerson | null) => void;
}): JSX.Element | null {
  const { t, locale } = useT();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminUserRole>("user");
  const [groups, setGroups] = useState<GroupSummaryOut[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [created, setCreated] = useState<CreatedPerson | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The person, once the server has created them. State, not a ref: the
  // Back button reads it.
  const [createdId, setCreatedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCreatedId(null);
    setStep(1);
    setName("");
    setEmail("");
    setCreated(null);
    setRole("user");
    setPicked(new Set());
    setError(null);
  }, [open]);

  const create = useCallback(
    async (groupIds: string[]): Promise<void> => {
      setBusy(true);
      setError(null);
      // Kept in a local too: state set inside the callback is not visible
      // until the next render, and onCreated needs it now.
      let outcome: CreatedPerson | null = created;
      try {
        await createThenJoin(
          createdId,
          async () => {
            const out = await adminUsersApi.create({
              email: email.trim(),
              name: name.trim(),
              locale,
              role,
            });
            outcome = { email: out.email, mail: out.mail };
            setCreated(outcome);
            return out;
          },
          (groupId, userId) => groupsApi.addMember(groupId, userId),
          groupIds,
          setCreatedId,
        );
        onCreated(outcome);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [createdId, created, email, name, locale, role, onCreated],
  );

  // Once the person exists, closing is "created": the caller refreshes
  // its list, or the new person would be invisible until a reload.
  const close = (): void => {
    if (createdId !== null) onCreated(created);
    else onClose();
  };

  const advance = async (): Promise<void> => {
    if (name.trim() === "" || email.trim() === "") {
      setError(t("access.people.form.fillRequired"));
      return;
    }
    setError(null);
    if (role === "admin") {
      // An admin bypasses every permission check, so a group would
      // decide nothing for them. Skipping the step is the honest
      // shortcut; offering it and ignoring it would not be.
      await create([]);
      return;
    }
    setStep(2);
    if (groups === null) {
      try {
        setGroups((await groupsApi.list()).groups);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  if (!open) return null;

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : close())}
      title={t("access.people.form.createTitle", { step, total: 2 })}
      size="md"
      footer={
        step === 1 ? (
          <div className="flex justify-end gap-2">
            <Button ghost size="sm" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => void advance()}
            >
              {t("access.people.form.next")}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button
              ghost
              size="sm"
              onClick={() => setStep(1)}
              disabled={busy || createdId !== null}
            >
              {t("access.people.form.back")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => void create([...picked])}
            >
              {t("common.create")}
            </Button>
          </div>
        )
      }
    >
      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      {step === 1 ? (
        <div className="mt-3 space-y-4">
          <FormField label={t("access.people.form.name")} required>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
            />
          </FormField>
          <FormField label={t("access.people.form.email")} required>
            <Input
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
            />
          </FormField>
          <FormField label={t("access.people.form.role")}>
            <Select
              aria-label={t("access.people.form.role")}
              value={role}
              options={ADMIN_USER_ROLES.map(
                (r): SelectOption => ({ value: r, label: r }),
              )}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setRole(e.target.value as AdminUserRole)
              }
            />
          </FormField>
          <Callout tone="info" title={t("access.people.form.inviteTitle")}>
            <Mail size={12} className="mr-1 inline align-text-bottom" />
            {t("access.people.form.inviteHint")}
          </Callout>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <Callout tone="info" title={t("access.people.form.groupsTitle")}>
            {t("access.people.form.groupsHint")}
          </Callout>

          {groups === null ? (
            <p className="text-[13px] text-neutral-500">{t("common.loading")}</p>
          ) : groups.length === 0 ? (
            <Callout tone="warning" title={t("access.people.form.noGroupsTitle")}>
              {t("access.people.form.noGroupsHint")}
            </Callout>
          ) : (
            <ul className="max-h-72 space-y-1 overflow-y-auto">
              {groups.map((g) => {
                const on = picked.has(g.id);
                return (
                  <li key={g.id}>
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setPicked((prev) => {
                          const next = new Set(prev);
                          if (next.has(g.id)) next.delete(g.id);
                          else next.add(g.id);
                          return next;
                        })
                      }
                      className={
                        on
                          ? "flex w-full items-center gap-2 rounded-md border border-primary-light bg-primary-soft px-3 py-1.5 text-left text-sm font-semibold text-primary-ink"
                          : "flex w-full items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                      }
                    >
                      {on ? (
                        <Check size={14} strokeWidth={3} aria-hidden="true" />
                      ) : (
                        <Plus size={14} strokeWidth={3} aria-hidden="true" />
                      )}
                      <span className="min-w-0 flex-1 truncate">{g.name}</span>
                      <span className="truncate font-mono text-[13px] text-neutral-500">
                        {g.scope_id === SCOPE_ALL
                          ? t("access.scopes.all")
                          : g.scope_id}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {picked.size === 0 && (
            <Callout tone="warning" title={t("access.people.form.noPickTitle")}>
              {t("access.people.form.noPick")}
            </Callout>
          )}
        </div>
      )}
    </Modal>
  );
}
